import { Circle, CircleDot } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import type { NotificationsApi } from '@/modules/notifications/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Heading } from '@/modules/ui/components/heading/Heading';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { PopoverClose } from '@/modules/ui/components/popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { daysSince } from '@/utils/dateUtils';

const notificationTypeColors: Record<NotificationsApi['type'], string> = {
  Information: 'bg-primary-600',
  Success: 'bg-semantic-560',
  Warning: 'bg-semantic-540',
  Failed: 'bg-semantic-550',
};

interface NotificationItemProps {
  notification: NotificationsApi;
  href: string | undefined;
  onToggleReadStatus: (notificationId: string, isRead: boolean) => void;
}

export const NotificationItem = ({
  notification,
  onToggleReadStatus,
  href,
}: NotificationItemProps) => {
  const rootComponentClassName =
    'flex min-w-0 flex-1 cursor-pointer flex-col gap-1';

  // keep the popover open when clicking + CMD key on a link
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const RootComponent = (children: React.ReactNode) =>
    href ? (
      <PopoverClose asChild>
        <Link
          to={href}
          className={rootComponentClassName}
          onClick={handleLinkClick}
        >
          {children}
        </Link>
      </PopoverClose>
    ) : (
      <div className={rootComponentClassName}>{children}</div>
    );

  return (
    <div
      className={cn(
        'hover:bg-secondary-150 w-full p-4 text-left transition-colors',
      )}
    >
      <div className="flex gap-2">
        <div
          className={cn(
            'mt-1.5 size-2 shrink-0 rounded-full',
            notificationTypeColors[notification.type] ?? 'bg-neutral-300',
          )}
          title={notification.type}
        />

        {RootComponent(
          <>
            <Heading variant="h3" className="text-foreground text-xs">
              {notification.title}
            </Heading>
            <Paragraph className="text-secondary-foreground text-xs">
              {notification.content}
            </Paragraph>
            <Paragraph className="text-muted-foreground text-xs">
              {daysSince(notification.createdAt)}
            </Paragraph>
          </>,
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="icon"
              size="icon"
              className={cn(
                notification.readUtc ? 'text-primary-600' : 'text-neutral-400',
              )}
              aria-label={
                notification.readUtc ? 'Mark as unread' : 'Mark as read'
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleReadStatus(
                  notification.id,
                  Boolean(notification.readUtc),
                );
              }}
            >
              {notification.readUtc ? (
                <CircleDot size={16} />
              ) : (
                <Circle size={16} />
              )}
            </Button>
          </TooltipTrigger>

          <TooltipContent>
            {notification.readUtc ? 'Mark as unread' : 'Mark as read'}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
